/**
 * Hook for generating assignment information using AI and saving it to a lead
 *
 * @example
 * ```tsx
 * const generateAssignment = useGenerateAssignment();
 *
 * // Generate from URL and save to lead
 * const handleGenerateFromUrl = async () => {
 *   const result = await generateAssignment.mutateAsync({
 *     path: {
 *       organizationID: "org-123",
 *       leadID: "lead-456"
 *     },
 *     query: { url: "https://example.com/job-posting" },
 *     body: {}
 *   });
 *   console.log(result.skills.technical_required);
 * };
 *
 * // Generate from raw text and save to lead
 * const handleGenerateFromText = async () => {
 *   const result = await generateAssignment.mutateAsync({
 *     path: {
 *       organizationID: "org-123",
 *       leadID: "lead-456"
 *     },
 *     body: {
 *       info: "Job description text here..."
 *     }
 *   });
 * };
 * ```
 */

import {
  type UseMutationOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { toast } from "sonner";
import {
  PostV1OrganizationsByOrganizationIdLeadsByLeadIdAssignmentGenerateData,
  PostV1OrganizationsByOrganizationIdLeadsByLeadIdAssignmentGenerateError,
  PostV1OrganizationsByOrganizationIdLeadsByLeadIdAssignmentGenerateResponse,
} from "../../../client";
import { postV1OrganizationsByOrganizationIdLeadsByLeadIdAssignmentGenerateMutation } from "../../../client/@tanstack/react-query.gen";
import { getLeadsQueryKey } from "./utils";

export const useGenerateAssignment = (
  options: UseMutationOptions<
    PostV1OrganizationsByOrganizationIdLeadsByLeadIdAssignmentGenerateResponse,
    PostV1OrganizationsByOrganizationIdLeadsByLeadIdAssignmentGenerateError,
    PostV1OrganizationsByOrganizationIdLeadsByLeadIdAssignmentGenerateData
  > = {},
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...postV1OrganizationsByOrganizationIdLeadsByLeadIdAssignmentGenerateMutation(),
    ...options,

    onSuccess: (data, variables, context) => {
      toast.success("Assignment information generated successfully");
      options.onSuccess?.(data, variables, context);
    },

    onError: (error, variables, context) => {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to generate assignment: ${errorMessage}`);
      options.onError?.(error, variables, context);
    },

    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: getLeadsQueryKey(variables.path.organizationID),
      });
      options.onSettled?.(data, error, variables, context);
    },
  });
};

/**
 * Helper function to create properly typed options for the mutation
 *
 * @example
 * ```tsx
 * const generateAssignment = useGenerateAssignment();
 *
 * // Generate from URL
 * const options = createGenerateAssignmentOptions(
 *   "org-123",
 *   "lead-456",
 *   { url: "https://example.com/job" }
 * );
 *
 * // Generate from text
 * const optionsFromText = createGenerateAssignmentOptions(
 *   "org-123",
 *   "lead-456",
 *   { info: "Job description text..." }
 * );
 *
 * await generateAssignment.mutateAsync(options);
 * ```
 */
export const createGenerateAssignmentOptions = (
  organizationId: string,
  leadId: string,
  input: { url: string } | { info: string },
): PostV1OrganizationsByOrganizationIdLeadsByLeadIdAssignmentGenerateData => {
  if ("url" in input) {
    return {
      path: { organizationID: organizationId, leadID: leadId },
      query: {
        url: input.url,
      },
      body: {},
    };
  }

  return {
    path: { organizationID: organizationId, leadID: leadId },
    body: { info: input.info },
  };
};
